import {Option} from "ts-option";
import User from "../../model/user/User";
import {DayViewFilters} from "./model/DayViewFilters";
import {AppointmentListFilters} from "./model/AppointmentListFilters";
import {RoutingMapFilters} from "./model/RoutingMapFilters";
import {FullCalendarFilters} from "./model/FullCalendarFilters";
import LeadEventCategoryType from "../../model/lead/event/LeadEventCategoryType";
import JobEventCategoryType from "../../model/job/event/JobEventCategoryType";
import {UsersListFilter} from "./model/UsersListFilter";
import {WorkspaceName} from "../../components/v5/table/workspace/WorkspaceContainer";
import {ContactsListFilter} from "./model/ContactsListFilter";
import {AssetCategoriesListFilter} from "./model/AssetCategoriesListFilter";
import Filter from "../../model/workspace/Filter";
import {ColumnDefinition} from "../../model/workspace/ColumnDefinition";
import InvoiceEventCategoryType from "../../model/invoice/event/InvoiceEventCategoryType";
import {isDefined} from "../../utils/Utils";

const leadActivityFiltersKey = "leadActivityFiltersKey";
const jobActivityFiltersKey = "jobActivityFiltersKey";
const jobStreamLeadActivityFiltersKey = "jobStreamLeadActivityFiltersKey";
const jobStreamInvoiceActivityFiltersKey = "jobStreamInvoiceActivityFiltersKey";
const assetSortOrderKey = "assetSortOrderKey";
const dayViewFiltersKey = "dayViewFiltersKey";
const appointmentListFiltersKey = "appointmentListFiltersKey";
const routingMapFiltersKey = "routingMapFiltersKey";
const fullCalendarFiltersKey = "fullCalendarFiltersKey";
const selectedWorkspaceViewIdKey = "selectedWorkspaceViewIdKey";
const assetsListFiltersKey = "assetsListFiltersKey";
const assetCategoriesListFiltersKey = "assetCategoriesListFiltersKey";
const usersListFiltersKey = "usersListFiltersKey";
const contactsListFiltersKey = "contactsListFiltersKey";
const _ = require('lodash');

/**
 * Helper class for storing data in browser local storage.
 */
export default class LocalStorage {
	/**
	 * User for which store data.
	 */
	static user: Option<User>;

	/**
	 * Save workspace filters in browser local storage.
	 * @param {Filter[]} data
	 * @param {string} workspaceName
	 */
	static saveWorkspaceFilters(workspaceName: string, data: Filter[]) {
		localStorage.setItem(
			`filters_${workspaceName}`,
			JSON.stringify(data.map(d => {
				return {
					...d.toJson(),
					columnDefinition: d.columnDefinition.toJson()
				}
			}))
		);
	}

	/**
	 * Get workspace filters from browser local storage.
	 * @returns Filter[]
	 */
	static getWorkspaceFilters(workspaceName: string): Filter[] {
		const rawFilters = localStorage.getItem(`filters_${workspaceName}`);
		if (rawFilters != null) {
			const filtersJson: any[] = JSON.parse(rawFilters);
			return filtersJson.map(item => Filter.fromJson(
				item,
				ColumnDefinition.fromJson({
					...item.columnDefinition,
					filterable: isDefined(item.columnDefinition.filterable) ? item.columnDefinition.filterable : true
				})));
		}
		else return [];
	}

	/**
	 * Save workspace search query in browser local storage.
	 * @param {string} data
	 * @param {string} workspaceName
	 */
	static saveWorkspaceSearch(workspaceName: string, data: string) {
		localStorage.setItem(
			`search_${workspaceName}`,
			JSON.stringify(data)
		);
	}

	/**
	 * Get workspace search query from browser local storage.
	 * @returns string
	 */
	static getWorkspaceSearch(workspaceName: string): string {
		const rawFilters = localStorage.getItem(`search_${workspaceName}`);
		if (rawFilters !== null) {
			return JSON.parse(rawFilters);
		}
		else return null;
	}

	/**
	 * Removes workspace filters and search query from browser local storage.
	 */
	static clearWorkspaceFilters() {
		_.forIn(window.localStorage, (value: string, key: string) => {
			if (true === _.startsWith(key, 'filters_') || true === _.startsWith(key, 'search_')) {
				window.localStorage.removeItem(key);
			}
		});
	}

	/**
	 * Save lead activity filters in browser local storage.
	 * @param {string[]} data
	 */
	static saveLeadActivityFilterIds(data: string[]) {
		LocalStorage.user.map(u => {
			localStorage.setItem(LocalStorage.getKeyForUser(leadActivityFiltersKey), JSON.stringify(data))
		});
	}

	/**
	 * Get lead activity filters from browser local storage.
	 * @returns string[].
	 */
	static getLeadActivityFilterIds(): string[] {
		if (LocalStorage.user.isDefined) {
			return JSON.parse(localStorage.getItem(this.getKeyForUser(leadActivityFiltersKey))) ||
				LeadEventCategoryType.getAllCategories().map((t: LeadEventCategoryType) => t.id);
		}
		else {
			return LeadEventCategoryType.getAllCategories().map((t: LeadEventCategoryType) => t.id);
		}
	}

	/**
	 * Save job activity filters in browser local storage.
	 * @param {string[]} data
	 */
	static saveJobActivityFilterIds(data: string[]) {
		LocalStorage.user.map(u => {
			localStorage.setItem(LocalStorage.getKeyForUser(jobActivityFiltersKey), JSON.stringify(data))
		});
	}

	/**
	 * Get job activity filters from browser local storage.
	 * @returns string[].
	 */
	static getJobActivityFilterIds(): string[] {
		if (LocalStorage.user.isDefined) {
			return JSON.parse(localStorage.getItem(this.getKeyForUser(jobActivityFiltersKey))) ||
				JobEventCategoryType.getAllCategories().map((t: JobEventCategoryType) => t.id);
		}
		else {
			return JobEventCategoryType.getAllCategories().map((t: JobEventCategoryType) => t.id);
		}
	}

	/**
	 * Save job stream lead activity filters in browser local storage.
	 * @param {string[]} data
	 */
	static saveJobStreamLeadActivityFilterIds(data: string[]) {
		LocalStorage.user.map(u => {
			localStorage.setItem(LocalStorage.getKeyForUser(jobStreamLeadActivityFiltersKey), JSON.stringify(data))
		});
	}

	/**
	 * Get job stream lead activity filters from browser local storage.
	 * @returns string[].
	 */
	static getJobStreamLeadActivityFilterIds(): string[] {
		if (LocalStorage.user.isDefined) {
			return JSON.parse(localStorage.getItem(this.getKeyForUser(jobStreamLeadActivityFiltersKey))) || [];
		}
		else {
			return LeadEventCategoryType.getAllCategories().map((t: JobEventCategoryType) => t.id);
		}
	}

	/**
	 * Save job stream invoice activity filters in browser local storage.
	 * @param {string[]} data
	 */
	static saveJobStreamInvoiceActivityFilterIds(data: string[]) {
		LocalStorage.user.map(u => {
			localStorage.setItem(LocalStorage.getKeyForUser(jobStreamInvoiceActivityFiltersKey), JSON.stringify(data))
		});
	}

	/**
	 * Get job stream invoice activity filters from browser local storage.
	 * @returns string[].
	 */
	static getJobStreamInvoiceActivityFilterIds(): string[] {
		if (LocalStorage.user.isDefined) {
			return JSON.parse(localStorage.getItem(this.getKeyForUser(jobStreamInvoiceActivityFiltersKey))) || [];
		}
		else {
			return InvoiceEventCategoryType.getAllCategories().map((t: InvoiceEventCategoryType) => t.id);
		}
	}

	/**
	 * Save day view filters in browser local storage.
	 * @param {DayViewFilters} data
	 */
	static saveDayViewFilters(data: DayViewFilters) {
		LocalStorage.user.map(u => {
			localStorage.setItem(LocalStorage.getKeyForUser(dayViewFiltersKey), JSON.stringify(data))
		});
	}

	/**
	 * Get day view filters from browser local storage.
	 * @returns DayViewFilters.
	 */
	static getDayViewFilters(): DayViewFilters {
		if (LocalStorage.user.isDefined) {
			const data: any = JSON.parse(localStorage.getItem(this.getKeyForUser(dayViewFiltersKey)));
			return DayViewFilters.getFromLocalStorageData(data);
		}
		else {
			return DayViewFilters.default();
		}
	}

	/**
	 * Save appointment list filters in browser local storage.
	 * @param {AppointmentListFilters} data
	 */
	static saveAppointmentListFilters(data: AppointmentListFilters) {
		LocalStorage.user.map(u => {
			localStorage.setItem(LocalStorage.getKeyForUser(appointmentListFiltersKey), JSON.stringify(data))
		});
	}

	/**
	 * Get appointment list filters from browser local storage.
	 * @returns AppointmentListFilters.
	 */
	static getAppointmentListFilters(): AppointmentListFilters {
		if (LocalStorage.user.isDefined) {
			const data: any = JSON.parse(localStorage.getItem(this.getKeyForUser(appointmentListFiltersKey)));
			return AppointmentListFilters.getFromLocalStorageData(data);
		}
		else {
			return AppointmentListFilters.default();
		}
	}

	/**
	 * Save routing map filters in browser local storage.
	 * @param {RoutingMapFilters} data
	 */
	static saveRoutingMapFilters(data: RoutingMapFilters) {
		LocalStorage.user.map(u => {
			localStorage.setItem(LocalStorage.getKeyForUser(routingMapFiltersKey), JSON.stringify(data))
		});
	}

	/**
	 * Get routing map filters from browser local storage.
	 * @returns RoutingMapFilters.
	 */
	static getRoutingMapFilters(): RoutingMapFilters {
		if (LocalStorage.user.isDefined) {
			const data: any = JSON.parse(localStorage.getItem(this.getKeyForUser(routingMapFiltersKey)));
			return RoutingMapFilters.getFromLocalStorageData(data);
		}
		else {
			return RoutingMapFilters.default();
		}
	}

	/**
	 * Save full calendar filters in browser local storage.
	 * @param {FullCalendarFilters} data
	 */
	static saveFullCalendarFilters(data: FullCalendarFilters) {
		LocalStorage.user.map(u => {
			localStorage.setItem(LocalStorage.getKeyForUser(fullCalendarFiltersKey), JSON.stringify(data))
		});
	}

	/**
	 * Get full calendar filters from browser local storage.
	 * @returns FullCalendarFilters.
	 */
	static getFullCalendarFilters(): FullCalendarFilters {
		if (LocalStorage.user.isDefined) {
			const data: any = JSON.parse(localStorage.getItem(this.getKeyForUser(fullCalendarFiltersKey)));
			return FullCalendarFilters.getFromLocalStorageData(data);
		}
		else {
			return FullCalendarFilters.default();
		}
	}

	/**
	 * Save asset categories list filters in browser local storage.
	 * @param {AssetCategoriesListFilter} data
	 */
	static saveAssetCategoriesListFilters(data: AssetCategoriesListFilter) {
		LocalStorage.user.map(u => {
			localStorage.setItem(LocalStorage.getKeyForUser(assetCategoriesListFiltersKey), JSON.stringify(data))
		});
	}

	/**
	 * Get asset categories list filter from local storage.
	 */
	static getAssetCategoriesListFilter(): AssetCategoriesListFilter {
		if (LocalStorage.user.isDefined) {
			const data: any = JSON.parse(localStorage.getItem(this.getKeyForUser(assetCategoriesListFiltersKey)));
			return AssetCategoriesListFilter.getFromLocalStorageData(data)
		}
		else {
			return AssetCategoriesListFilter.empty();
		}
	}

	/**
	 * Save users list filters in browser local storage.
	 * @param {UsersListFilter} data
	 */
	static saveUsersListFilters(data: UsersListFilter) {
		LocalStorage.user.map(u => {
			localStorage.setItem(LocalStorage.getKeyForUser(usersListFiltersKey), JSON.stringify(data))
		});
	}

	/**
	 * Get users list filter from local storage.
	 */
	static getUsersListFilter(): UsersListFilter {
		if (LocalStorage.user.isDefined) {
			const data: any = JSON.parse(localStorage.getItem(this.getKeyForUser(usersListFiltersKey)));
			return UsersListFilter.getFromLocalStorageData(data)
		}
		else {
			return UsersListFilter.empty();
		}
	}

	/**
	 * Save contacts list filters in browser local storage.
	 * @param {UsersListFilter} data
	 */
	static saveContactsListFilters(data: ContactsListFilter) {
		LocalStorage.user.map(u => {
			localStorage.setItem(LocalStorage.getKeyForUser(contactsListFiltersKey), JSON.stringify(data))
		});
	}

	/**
	 * Get contacts list filter from local storage.
	 */
	static getContactsListFilter(): ContactsListFilter {
		if (LocalStorage.user.isDefined) {
			const data: any = JSON.parse(localStorage.getItem(this.getKeyForUser(contactsListFiltersKey)));
			return ContactsListFilter.getFromLocalStorageData(data);
		}
		else {
			return ContactsListFilter.empty();
		}
	}

	static saveSelectedWorkspaceView(workspaceName: WorkspaceName, viewId: string) {
		LocalStorage.user.map(() => {
			localStorage.setItem(LocalStorage.getKeyForUser(`${selectedWorkspaceViewIdKey}#${workspaceName}`), viewId)
		});
	}

	static getWorkspaceViewId(workspaceName: WorkspaceName): string {
		if (LocalStorage.user.isDefined) {
			return localStorage.getItem(this.getKeyForUser(`${selectedWorkspaceViewIdKey}#${workspaceName}`));
		}
		else {
			return null;
		}
	}

	static getKeyForUser(key: string) {
		return `${key}#${LocalStorage.user.map(u => u.id).orNull}`
	}

}